<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'
  import { computed, onMounted, ref, watch } from 'vue'

  // External library imports

  import { string } from 'yup'

  import { useGlobal } from '@common/core-layer/composables/useGlobal'

  import { useI18n } from 'vue-i18n'

  import CPButton from '@ui/components/CPButton.vue'

  import CPForm from '@ui/components/CPForm.vue'

  import CPFormItem from '@ui/components/CPFormItem.vue'

  import CPInput from '@ui/components/CPInput.vue'

  import CPText from '@ui/components/CPText.vue'

  // Internal imports
  import SignInWithGoogle from './SignInWithGoogle.vue'

  import SignInWithGoogleKollab from './SignInWithGoogleKollab.vue'

  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { useCaptchaChallenge } from '../../composables/useCaptchaChallenge'

  import { LoginEvents, Screens } from '../../types'

  import { getErrorMessage, isAccountBannedError } from '../../utils'
  import {
    buildSetPasswordScreenChange,
    isDeferredInPlacePasswordLoginResult,
    shouldShowSetPasswordAfterLoginResponse,
  } from '../../utils/inPlacePasswordDefer'

  import ReCaptcha from '../captcha/ReCaptcha.vue'

  // Props definitions
  const props = defineProps({
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
    email: {
      type: String,
      default: '',
    },
    isSsoDisabled: {
      type: Boolean,
      default: false,
    },
    /** When true, an unverified password opens Set Password. Not forwarded to Google sign-in. */
    deferSetPasswordSession: {
      type: Boolean,
      default: false,
    },
  })

  // Emits
  const emit = defineEmits<{
    (e: 'changeScreen', data: any): void
    (e: 'loginCallback', data: any): void
  }>()

  // Composables
  const { loginWithEmailPassword } = useAuth()

  const { isGokollab } = useGlobal()

  const { t: $t } = useI18n()

  // Refs and reactive variables
  const errorMessage = ref()

  const loading = ref(false)

  const loginDisabled = ref(false)

  const { showRecaptcha, reCaptchaToken, onVerify, onError, onExpired, handleRateLimit } = useCaptchaChallenge(loginDisabled)

  const passwordError = ref('')

  const emailError = ref('')

  const formRef = ref()

  const formValue = ref({
    email: props.email || '',
    password: '',
  })

  const rules = {
    email: {
      required: true,
      asyncValidator: async (_: any, value: string) => {
        emailError.value = ''

        if (!value) {
          emailError.value = $t('auth.validation.emailRequired')
          return Promise.reject(new Error($t('auth.validation.emailRequired')))
        }

        try {
          await string().email($t('auth.validation.emailInvalid')).required($t('auth.validation.emailRequired')).validate(value)
          return Promise.resolve()
        } catch {
          emailError.value = $t('auth.validation.emailInvalid')
          return Promise.reject(new Error(emailError.value))
        }
      },
      trigger: ['input', 'blur'],
    },
    password: {
      required: true,
      min: 8,
      validator: (_: unknown, value: string) => {
        passwordError.value = ''

        if (!value) {
          passwordError.value = $t('auth.validation.passwordRequired')
          return false
        }

        if (value.length < 8) {
          passwordError.value = $t('auth.validation.passwordMinLength')
          return false
        }

        return true
      },
      trigger: ['input', 'blur'],
    },
  }

  const isFormValid = computed(() => {
    return (
      formValue.value.email && formValue.value.password && formValue.value.password.length >= 8 && !emailError.value && !passwordError.value
    )
  })

  // Lifecycle hooks
  onMounted(() => {
    if (props.email) {
      formValue.value.email = props.email
    }
  })

  // Watch effects
  watch(
    () => props.email,
    (newEmail: string) => {
      if (newEmail) {
        formValue.value.email = newEmail
      }
    }
  )

  watch(formValue.value, () => {
    errorMessage.value = null
  })

  watch(
    () => formValue.value.password,
    () => {
      passwordError.value = ''
    }
  )

  watch(
    () => formValue.value.email,
    () => {
      emailError.value = ''
    }
  )

  async function validateForm() {
    passwordError.value = ''
    try {
      await formRef.value.getForm().validate()
      return true
    } catch (e: unknown) {
      console.error('error', e)

      // Check if there are password-specific validation errors
      const form = formRef.value.getForm()
      const passwordFieldErrors = form.getFieldError('password')

      if (passwordFieldErrors && passwordFieldErrors.length > 0) {
        passwordError.value = passwordFieldErrors[0]
      }

      return false
    }
  }

  async function handleValidateClick(e: Event) {
    e.preventDefault()
    if (loginDisabled.value) {
      return
    }
    errorMessage.value = null
    const result = await validateForm()
    if (!result) {
      return
    }
    loading.value = true
    try {
      const email = formValue.value.email.trim()
      const result = await loginWithEmailPassword(email, formValue.value.password.trim(), reCaptchaToken.value, {
        deferSessionUntilInPlacePasswordSet: props.deferSetPasswordSession,
      })

      if (isDeferredInPlacePasswordLoginResult(props.deferSetPasswordSession, result)) {
        emit('changeScreen', buildSetPasswordScreenChange(email, result.data))
        showRecaptcha.value = false
        loading.value = false
        return
      }

      const data = result.data
      if (data) {
        if (data.multipleUsers) {
          emit('changeScreen', {
            page: Screens.SELECT_USER_MULTI_USER,
            data: {
              ...formValue.value,
              multiUser: data.multipleUsers,
            },
          })
        } else if (data.token) {
          if (props.deferSetPasswordSession && shouldShowSetPasswordAfterLoginResponse(data)) {
            emit('changeScreen', buildSetPasswordScreenChange(email, data))
          } else {
            emit('loginCallback', {
              eventType: LoginEvents.LOGIN_SUCCESSFUL,
              data: { ...data },
            })
          }
        }
      }
      showRecaptcha.value = false
    } catch (error: any) {
      console.error('Login error:', error)
      if (!handleRateLimit(error)) {
        errorMessage.value = isAccountBannedError(error) ? $t('auth.authState.bannedMessage') : getErrorMessage(error)
      }
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div>
    <SignInWithGoogleKollab
      v-if="!props.isSsoDisabled && isGokollab"
      login-type="SIGN_IN"
      :referrer-user="referrerUser"
      @changeScreen="(val: any) => emit('changeScreen', val)"
      @loginCallback="(val: any) => emit('loginCallback', val)"
    />
    <SignInWithGoogle
      v-else-if="!props.isSsoDisabled"
      login-type="SIGN_IN"
      :referrer-user="referrerUser"
      @changeScreen="(val: any) => emit('changeScreen', val)"
      @loginCallback="(val: any) => emit('loginCallback', val)"
    />

    <CPForm ref="formRef" class="" :rules="rules" :model="formValue" @keyup.enter="handleValidateClick">
      <CPFormItem
        :id="createElementId('login-modal', 'email', 'form-item')"
        class="w-full"
        :label="$t('auth.email')"
        required
        path="email"
        :show-require-mark="true"
        label-size="md"
      >
        <CPInput
          :id="createElementId('login-modal', 'email', 'input')"
          v-model="formValue.email"
          size="lg"
          :placeholder="$t('auth.emailPlaceholder')"
        />
        <template #feedback>
          <CPText :id="createElementId('login-modal', 'email-field', 'feedback')" size="md" :class="{ 'text-danger': emailError }">
            {{ emailError }}
          </CPText>
        </template>
      </CPFormItem>

      <CPFormItem
        :id="createElementId('login-modal', 'password', 'form-item')"
        class="w-full"
        :label="$t('auth.password')"
        required
        path="password"
        :show-require-mark="true"
        label-size="md"
      >
        <CPInput
          :id="createElementId('login-modal', 'password', 'input')"
          v-model="formValue.password"
          size="lg"
          type="password"
          show-password-on="click"
          :placeholder="$t('auth.passwordPlaceholder')"
        />
        <template #feedback>
          <div class="flex w-full items-center justify-between">
            <CPText :id="createElementId('login-modal', 'password-field', 'feedback')" size="md" :class="{ 'text-danger': passwordError }">
              {{ passwordError }}
            </CPText>
            <CPButton
              :id="createElementId('login-modal', 'reset-password', 'button')"
              type="text"
              color="secondary"
              size="xs"
              class="ml-2 flex-shrink-0 cursor-pointer hover:underline"
              @click="
                emit('changeScreen', {
                  page: Screens.FORGOT_PASSWORD,
                  data: { email: formValue.email },
                })
              "
            >
              <CPText size="md" weight="semibold" html-tag="span">
                {{ $t('auth.resetPassword') }}
              </CPText>
            </CPButton>
          </div>
        </template>
      </CPFormItem>

      <div v-if="errorMessage" class="text-danger mb-3 w-full">
        <CPText :id="createElementId('login-modal', 'error', 'message')" size="sm">{{ errorMessage }}</CPText>
      </div>

      <ReCaptcha v-if="showRecaptcha" class="flex w-full justify-center" @expired="onExpired" @verify="onVerify" @error="onError" />

      <CPButton
        :id="createElementId('login-modal', 'email-password', 'button')"
        class="w-full shadow-sm mt-1"
        type="primary"
        size="lg"
        :disabled="loginDisabled"
        :loading="loading"
        :keyboard="true"
        @click="handleValidateClick"
      >
        {{ $t('auth.login') }}
      </CPButton>

      <CPButton
        :id="createElementId('login-modal', 'secure-code', 'button')"
        class="w-full mt-2"
        type="secondary"
        size="lg"
        @click="
          () =>
            emit('changeScreen', {
              page: Screens.LOGIN_WITH_SECURE_CODE,
              data: { email: formValue.email },
            })
        "
      >
        {{ $t('auth.loginWithSecureCode') }}
      </CPButton>

      <div class="text-secondary flex w-full justify-center items-center gap-1 mt-4">
        <CPText size="md" weight="regular" html-tag="span">
          {{ $t('auth.newUser') }}
        </CPText>
        <CPButton
          :id="createElementId('login-modal', 'signup', 'button')"
          type="text"
          color="secondary"
          size="xs"
          class="cursor-pointer hover:underline"
          @click="
            emit('changeScreen', {
              page: Screens.SIGN_UP,
              data: { email: formValue.email },
            })
          "
        >
          <CPText size="md" weight="semibold" html-tag="span">
            {{ $t('auth.signUp') }}
          </CPText>
        </CPButton>
      </div>
    </CPForm>
  </div>
</template>
